import { GridColDef } from "@mui/x-data-grid-pro";
import {
  TextField,
  Autocomplete,
  Chip,
  IconButton,
  Box,
  Typography,
  Popover,
  Button,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { TFunction } from "i18next";
import {
  NewUserRow,
  RowErrorType,
  GroupOption,
} from "../../../../../../../redux/slices/users/managerCreation";
import { columnStyles as sx } from "./columns.styles";

export type { NewUserRow, RowErrorType };

interface ColumnsOptions {
  t: TFunction;
  sites: { id: string; name: string }[];
  roles: { id: string; name: string }[];
  groups: GroupOption[];
  onChangeEmail: (id: string, value: string) => void;
  onChangeSite: (
    id: string,
    value: { id: string; name: string } | null,
  ) => void;
  onChangeRole: (
    id: string,
    value: { id: string; name: string } | null,
  ) => void;
  onChangeGroups: (id: string, values: GroupOption[]) => void;
  onDeleteRow: (id: string) => void;
}

const RequiredHeader = ({ label }: { label: string }) => (
  <Box display="flex" alignItems="center" gap={0.25}>
    <Box component="span" sx={sx.requiredHeaderLabel}>
      {label}
    </Box>
    <Box component="span" sx={sx.requiredHeaderAsterisk}>
      *
    </Box>
  </Box>
);

const LockedPlaceholder = ({ text }: { text: string }) => (
  <Box display="flex" alignItems="center" gap={0.5} sx={sx.lockedBox}>
    <LockOutlinedIcon sx={sx.lockIcon} />
    <Typography variant="caption">{text}</Typography>
  </Box>
);

const InlineMessage = ({
  text,
  color,
}: {
  text: string;
  color: "error.main" | "warning.main";
}) => (
  <Box display="flex" alignItems="center" gap={0.5} mt={0.25} sx={{ color }}>
    <WarningAmberOutlinedIcon sx={sx.warningIcon} />
    <Typography variant="caption">{text}</Typography>
  </Box>
);

const DeleteCell = ({
  onConfirm,
  label,
}: {
  onConfirm: () => void;
  label: string;
}) => {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={sx.deleteButton}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2">{label}</Typography>
          <Box display="flex" gap={1} justifyContent="flex-end">
            <Button size="small" onClick={() => setAnchor(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                setAnchor(null);
                onConfirm();
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const EmailCell = ({
  email,
  errorType,
  id,
  onChangeEmail,
  t,
}: {
  email: string;
  errorType: RowErrorType | undefined;
  id: string;
  onChangeEmail: (id: string, value: string) => void;
  t: TFunction;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorRef = useRef<{ start: number | null; end: number | null }>({
    start: null,
    end: null,
  });

  useEffect(() => {
    if (
      inputRef.current &&
      cursorRef.current.start !== null &&
      cursorRef.current.end !== null
    ) {
      inputRef.current.setSelectionRange(
        cursorRef.current.start,
        cursorRef.current.end,
      );
    }
  });

  const isEmpty = email === "";

  return (
    <Box width="100%">
      <TextField
        fullWidth
        size="small"
        value={email}
        placeholder={t(k("email_placeholder"))}
        error={errorType === "invalid_format" || errorType === "duplicate"}
        inputRef={inputRef}
        onChange={(e) => {
          const spaces = (e.target.value.match(/ /g) ?? []).length;
          const value = e.target.value.replace(/ /g, "");
          cursorRef.current = {
            start: (e.target.selectionStart ?? value.length) - spaces,
            end: (e.target.selectionEnd ?? value.length) - spaces,
          };
          onChangeEmail(id, value);
        }}
        onKeyDown={(e) => {
          if (
            e.key === "ArrowLeft" ||
            e.key === "ArrowRight" ||
            e.key === "ArrowUp" ||
            e.key === "ArrowDown"
          ) {
            e.stopPropagation();
          }
        }}
        sx={sx.roundedInput}
      />
      {isEmpty && (
        <InlineMessage text={t(k("email_start"))} color="warning.main" />
      )}
      {errorType === "duplicate" && (
        <InlineMessage text={t(k("error_duplicate"))} color="error.main" />
      )}
      {errorType === "already_exists" && (
        <InlineMessage
          text={t(k("error_already_exists"))}
          color="warning.main"
        />
      )}
    </Box>
  );
};

const k = (key: string) => `Users_managers.table_v2.${key}`;

const cap = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const createNewUserColumns = ({
  t,
  sites,
  roles,
  groups,
  onChangeEmail,
  onChangeSite,
  onChangeRole,
  onChangeGroups,
  onDeleteRow,
}: ColumnsOptions): GridColDef<NewUserRow>[] => [
  {
    field: "email",
    headerName: cap(t(k("col_email"))),
    flex: 1.5,
    minWidth: 220,
    sortable: false,
    disableColumnMenu: true,
    renderHeader: () => <RequiredHeader label={cap(t(k("col_email")))} />,
    renderCell: (params) => (
      <EmailCell
        email={params.row.email}
        errorType={params.row.errorType}
        id={params.row.id}
        onChangeEmail={onChangeEmail}
        t={t}
      />
    ),
  },
  {
    field: "role",
    headerName: cap(t(k("col_role"))),
    flex: 1.2,
    minWidth: 180,
    sortable: false,
    disableColumnMenu: true,
    renderHeader: () => <RequiredHeader label={cap(t(k("col_role")))} />,
    renderCell: (params) => {
      const emailFilled = EMAIL_RE.test(params.row.email);
      const isExisting = params.row.status === "existing";

      if (!emailFilled || isExisting) {
        return (
          <Box width="100%">
            <Box sx={sx.disabledFieldBox}>
              <Typography variant="body2">
                {t(k("site_after_email"))}
              </Typography>
            </Box>
            <LockedPlaceholder text={t(k("site_fill_email"))} />
          </Box>
        );
      }

      return (
        <Box width="100%">
          <Autocomplete
            fullWidth
            size="small"
            options={roles}
            getOptionLabel={(o) => o.name}
            value={roles.find((r) => r.id === params.row.role) ?? null}
            onChange={(_, val) => onChangeRole(params.row.id, val)}
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                placeholder={t(k("role_placeholder"))}
                sx={sx.roundedInput}
              />
            )}
          />
          {params.row.errorType === "missing_role" && (
            <InlineMessage
              text={t(k("error_missing_role"))}
              color="warning.main"
            />
          )}
        </Box>
      );
    },
  },
  {
    field: "siteId",
    headerName: cap(t(k("col_site"))),
    flex: 1.2,
    minWidth: 180,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const emailFilled = EMAIL_RE.test(params.row.email);
      const isExisting = params.row.status === "existing";

      if (!emailFilled || isExisting) {
        return (
          <Box width="100%">
            <Box sx={sx.disabledFieldBox}>
              <Typography variant="body2">
                {t(k("site_after_email"))}
              </Typography>
            </Box>
            <LockedPlaceholder text={t(k("site_fill_email"))} />
          </Box>
        );
      }

      return (
        <Autocomplete
          fullWidth
          size="small"
          options={sites}
          getOptionLabel={(o) => o.name}
          value={sites.find((s) => s.id === params.row.siteId) ?? null}
          onChange={(_, val) => onChangeSite(params.row.id, val)}
          renderInput={(inputParams) => (
            <TextField
              {...inputParams}
              placeholder={t(k("site_placeholder"))}
              sx={sx.roundedInput}
            />
          )}
        />
      );
    },
  },
  {
    field: "assignedGroups",
    headerName: cap(t(k("col_groups"))),
    flex: 1.5,
    minWidth: 200,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const selectedGroups = params.row.groupsSelected;
      const emailFilled = EMAIL_RE.test(params.row.email);
      const isExisting = params.row.status === "existing";

      if (!emailFilled || isExisting) {
        return (
          <Box width="100%">
            <Box sx={sx.disabledFieldBox}>
              <Typography variant="body2">
                {t(k("site_after_email"))}
              </Typography>
            </Box>
            <LockedPlaceholder text={t(k("site_fill_email"))} />
          </Box>
        );
      }

      return (
        <Autocomplete
          fullWidth
          multiple
          size="small"
          options={groups}
          getOptionLabel={(o) => o.name}
          value={selectedGroups}
          onChange={(_, vals) => onChangeGroups(params.row.id, vals)}
          renderTags={(vals, getTagProps) =>
            vals.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.name}
                size="small"
              />
            ))
          }
          renderInput={(inputParams) => (
            <TextField
              {...inputParams}
              placeholder={t(k("groups_placeholder"))}
              sx={sx.roundedInput}
            />
          )}
        />
      );
    },
  },
  {
    field: "status",
    headerName: cap(t(k("col_status"))),
    width: 110,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const statusMap: Record<
        NewUserRow["status"],
        { label: string; color: string }
      > = {
        vide: { label: t(k("status_empty")), color: "text.secondary" },
        valid: { label: t(k("status_valid")), color: "success.main" },
        existing: { label: t(k("status_existing")), color: "warning.main" },
        error: { label: t(k("status_error")), color: "error.main" },
      };
      const { label, color } = statusMap[params.row.status];
      return (
        <Typography variant="body2" sx={{ color }}>
          {label}
        </Typography>
      );
    },
  },
  {
    field: "actions",
    headerName: "",
    width: 50,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <DeleteCell
        onConfirm={() => onDeleteRow(params.row.id)}
        label={t(k("delete_confirm"))}
      />
    ),
  },
];

export default createNewUserColumns;
